import Head from 'next/head';
import { useTranslations } from 'next-intl';
import { SignupPage, SignupPageProps } from '@zapier/auth-pages';
import type { Invitation, InvitationResponse } from '@zapier/authentication';
import { parseDomainCaptureErrors } from '@zapier/authentication';
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import {
  getFacebookLeadAdsRedirectUrl,
  isFacebookLeadAdsUrl,
} from '../../security/securityHeaders';
import { urls } from '@zapier/identity-utils';
import { css } from '@emotion/react';
import { PageLayout } from '../../components';

import { init as initBuilderKit, safelyFetchSection } from '../../builder-kit';
import isEmpty from 'lodash/isEmpty';
import { DEFAULT_DOMAIN_CAPTURE_ERROR_MODAL_MESSAGE } from '../../../../authentication/src/components/global/DomainCaptureErrorModal';
import {
  DiscoveredDomainAccount,
  DomainDiscoveryResponse,
} from '@zapier/authentication/src/utils/discoverDomain';
import { captureException } from '@sentry/nextjs';
import { addTruthyParamsToUrl } from '@zapier/url-utils';
import { Metadata, PrefillData } from '@zapier/authentication/src/types';

initBuilderKit();

const visuallyHiddenH1 = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export default function SignUp({
  builderSections,
  invitationData,
  domainCaptureData,
  metadata,
  prefillData,
}: SignupPageProps) {
  const t = useTranslations('sign-up-page');

  return (
    <>
      <Head>
        <title>{t('seo-title')}</title>
        <meta name="description" content={t('seo-description')} />
        <link rel="canonical" href="https://zapier.com/sign-up" />
        <script src="https://cdn.zapier.com/packages/intercom/v1/index.js" />
      </Head>

      <PageLayout>
        <h1 css={visuallyHiddenH1}>{t('h1')}</h1>
        <SignupPage
          builderSections={builderSections}
          invitationData={invitationData}
          domainCaptureData={domainCaptureData}
          metadata={metadata}
          prefillData={prefillData}
        />
      </PageLayout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<SignupPageProps> = async (
  context: GetServerSidePropsContext
) => {
  const reqUrl = context.req.url || '';
  const parsedUrl = new URL(`https://zapier.com${reqUrl}`);

  // Get content from Builder.io
  const models = ['sign-up-left-side', 'sign-up-footer'];
  const builderSections = await Promise.all(
    models.map(model =>
      safelyFetchSection({
        model,
        userAttributes: {
          queryParams: encodeURIComponent(parsedUrl.search),
          urlPath: parsedUrl.pathname,
        },
      })
    )
  ).then(([leftSide, footer]) => {
    return {
      leftSide,
      footer,
    };
  });

  try {
    if (isFacebookLeadAdsUrl(reqUrl)) {
      const fbUrl = await getFacebookLeadAdsRedirectUrl(reqUrl);
      return {
        redirect: {
          permanent: false,
          destination: fbUrl,
        },
        props: {},
      };
    }
  } catch (err) {
    console.error(
      'Error trying to redirect to Facebook Lead Ads landing page',
      err
    );
  }

  try {
    const isCompanyAccountSetup = (context.query.next || '').includes(
      'app/company-account-setup'
    );

    if (isCompanyAccountSetup) {
      return {
        redirect: {
          destination:
            '/l/contact-sales?demo_source=original-company-account-setup',
        },
        props: {},
      };
    }
  } catch (err) {
    console.error(
      'Error trying to redirect to contact sales landing page',
      err
    );
  }

  // DomainCapture for sso return
  const domainCaptureEmail = context.query.domain_capture_email as string;
  const domainCaptureCode = context.query.domain_capture_code as string;
  const domainCaptureData = await callDiscoverDomain(
    context,
    domainCaptureEmail,
    domainCaptureCode
  );

  const metadata = {
    skipOnboarding: (context.query.skipOnboarding as string) || '',
    userProvisioningMethod:
      (context.query.userProvisioningMethod as string) || '',
  } as Metadata;

  const prefillData = {
    firstName: (context.query.firstName as string) || '',
    lastName: (context.query.lastName as string) || '',
    email: (context.query.email as string) || '',
  } as PrefillData;

  const code = context.query.invitation as string;
  if (code) {
    try {
      const invitation = await getInvitationByCode(context, code);

      return {
        props: {
          builderSections,
          invitationData: {
            invitation,
            isValid: Boolean(invitation?.status === 'pending'),
            code,
          },
          domainCaptureData,
          metadata,
          prefillData,
        },
      };
    } catch (err: any) {
      return {
        props: {
          builderSections,
          invitationData: {
            error: err.message || 'There was an unexpected error',
            isValid: false,
            code,
          },
          domainCaptureData,
          metadata,
          prefillData,
        },
      };
    }
  } else {
    // No invitation to fetch, return nothing for invitation
    return {
      props: {
        builderSections,
        domainCaptureData,
        metadata,
        prefillData,
      },
    };
  }
};

export type DomainCaptureProps = {
  isFromSsoReturn: boolean;
  errorModalMessage?: string | null;
  discoveredDomainAccount: DiscoveredDomainAccount | null;
};

async function callDiscoverDomain(
  context: GetServerSidePropsContext,
  email: string,
  code: string
): Promise<DomainCaptureProps> {
  let isFromSsoReturn = false;
  let errorModalMessage = null;
  let discoveredDomainAccount = null;

  if (isEmpty(email) != isEmpty(code)) {
    errorModalMessage = DEFAULT_DOMAIN_CAPTURE_ERROR_MODAL_MESSAGE;
  } else if (!isEmpty(email) && !isEmpty(code)) {
    isFromSsoReturn = true;

    try {
      const baseUrl = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
      const headers: HeadersInit = new Headers();
      headers.set('cookie', context.req.headers.cookie || '');

      const url = addTruthyParamsToUrl(
        `${baseUrl}${urls.DOMAIN_DISCOVERY_URL}`,
        {
          email,
          code,
        }
      );

      const response = await fetch(url, {
        headers,
      });
      const result = await response.json();

      if (result?.error) {
        // Look for possible error properties
        errorModalMessage = result?.error;
      } else {
        // Success
        const body = result as DomainDiscoveryResponse;

        if (
          body?.errors == null &&
          body?.account?.id != null &&
          body?.user?.id == null
        ) {
          discoveredDomainAccount = {
            id: body.account.id,
            name: body.account.name,
            logoUrl: body.account.logo_url,
            accessPolicy: body.account.access_policy,
            ssoUrl: body.account.sso_url,
            hasSamlSso: body.account.has_saml_sso,
            domain: email.split('@')[1],
            isDomainCaptureBlocked: body.account.is_domain_capture_blocked,
          };
        } else {
          errorModalMessage = parseDomainCaptureErrors(body?.errors);
          if (isEmpty(errorModalMessage)) {
            errorModalMessage = DEFAULT_DOMAIN_CAPTURE_ERROR_MODAL_MESSAGE;
          }
        }
      }
    } catch (err) {
      // This will catch things like network errors and the server being down
      captureException(err);
      if (typeof err === 'string') {
        errorModalMessage = err;
      } else if (err instanceof Error) {
        errorModalMessage = err.message;
      } else {
        errorModalMessage = DEFAULT_DOMAIN_CAPTURE_ERROR_MODAL_MESSAGE;
      }
    }
  }

  return { isFromSsoReturn, errorModalMessage, discoveredDomainAccount };
}

async function getInvitationByCode(
  context: GetServerSidePropsContext,
  code: string
): Promise<Invitation> {
  const baseUrl = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
  const headers: HeadersInit = new Headers();
  headers.set('cookie', context.req.headers.cookie || '');

  const response = await fetch(`${baseUrl}/api/v4/invitations/${code}`, {
    headers,
  });
  const result = await response.json();

  if (response.status !== 200) {
    // Look for possible error properties
    throw new Error(result?.error);
  }

  // Success
  const body = result as InvitationResponse;

  return {
    code,
    account: {
      id: body.account.id,
      logoUrl: body.account.logo_url,
      name: body.account.name,
      hasSamlSso: body.account.has_saml_sso,
    },
    ssoUrl: body.sso_url,
    email: body.email,
    emailFound: body.email_found || null,
    id: body.id,
    status: body.status,
    createdBy: body.created_by,
  };
}
